// src/pages/VehicleV2/VehicleSpareDeductionEntry.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/spareDeduction";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyyToYmd = (ddmmyyyy) => {
  const v = toStr(ddmmyyyy);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const ymdToDDMMYYYY = (ymd) => {
  const v = toStr(ymd);
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const todayToYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const todayToDDMMYYYY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isValidDDMMYYYY = (s) => {
  const v = toStr(s);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const clampPercentage = (raw) => {
  let v = toStr(raw).replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;
  const [a, b] = v.split(".");
  if (typeof b === "string") v = `${a}.${b.slice(0, 2)}`;
  if (v.length > 6) v = v.slice(0, 6);
  return v;
};

const VehicleSpareDeductionEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const spareId = useMemo(
    () =>
      toInt(
        searchParams.get("sparedeductionperid") ||
          searchParams.get("spareId") ||
          searchParams.get("id"),
        0
      ),
    [searchParams]
  );

  const isEdit = spareId > 0;
  const todayYmd = useMemo(() => todayToYMD(), []);

  const [subtypes, setSubtypes] = useState([]);
  const [subtypeId, setSubtypeId] = useState("Select");
  const [percentage, setPercentage] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const loadSubtypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/subtypes`, {
        params: { mode: isEdit ? "edit" : "add" },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load sub vehicle types");
      const list = Array.isArray(data?.subtypes) ? data.subtypes : [];
      setSubtypes(
        [{ id: "Select", name: "Select" }].concat(
          list.map((x) => ({ id: String(x.id), name: String(x.name) }))
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load sub vehicle types");
      setSubtypes([{ id: "Select", name: "Select" }]);
    }
  }, [isEdit, commonHeaders]);

  const loadEditData = useCallback(async () => {
    if (!isEdit) return;
    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { spareId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load record");
      const r = data?.data || {};
      setSubtypeId(r.subtypeId != null ? String(r.subtypeId) : "Select");
      setPercentage(r.percentage != null ? String(r.percentage) : "");
      setEntryDate(toStr(r.entryDate));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load record");
    } finally {
      setPageLoading(false);
    }
  }, [isEdit, spareId, commonHeaders]);

  useEffect(() => {
    loadSubtypes();
  }, [loadSubtypes]);

  useEffect(() => {
    if (isEdit) loadEditData();
    else setEntryDate(todayToDDMMYYYY());
  }, [isEdit, loadEditData]);

  const validate = () => {
    if (!subtypeId || String(subtypeId) === "Select") {
      toast.error("Please select Vehicle Sub Type");
      return false;
    }
    if (!toStr(entryDate) || !isValidDDMMYYYY(entryDate)) {
      toast.error("Please select a valid Effect Date");
      return false;
    }
    const [dd, mm, yyyy] = entryDate.split("/").map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate.getTime() > today.getTime()) {
      toast.error("Select Current Date or Previous date");
      return false;
    }
    const pct = Number(percentage);
    if (!Number.isFinite(pct) || pct <= 0) {
      toast.error("Percentage should be greater than 0");
      return false;
    }
    if (pct > 100) {
      toast.error("Percentage should not be greater than 100");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          spareId: spareId || 0,
          subtypeId: toInt(subtypeId, 0),
          entryDate: ddmmyyyyToYmd(entryDate),
          percentage: Number(percentage),
          loginUserId,
          branchId,
          branchType: branch,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(data?.message || "Saved successfully");
      setTimeout(() => navigate("/vehicle/VehicleSpareDeductionListing"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SPARE DEDUCTION</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit ? "Edit Spare Deduction" : "Add Spare Deduction"}
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/vehicle/VehicleSpareDeductionListing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  loading || pageLoading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Deduction Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Vehicle Sub Type *</label>
                <select
                  value={subtypeId}
                  onChange={(e) => setSubtypeId(e.target.value)}
                  disabled={loading || pageLoading || isEdit}
                  className={inputClass}
                >
                  {subtypes.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Percent *</label>
                <input
                  type="text"
                  value={percentage}
                  onChange={(e) => setPercentage(clampPercentage(e.target.value))}
                  onBlur={() => setPercentage(clampPercentage(percentage))}
                  className={inputClass}
                  placeholder="Enter percentage..."
                  disabled={loading || pageLoading}
                  inputMode="decimal"
                  maxLength={6}
                />
              </div>
              <div>
                <label className={labelClass}>Effect Date *</label>
                <input
                  type="date"
                  value={ddmmyyyyToYmd(entryDate)}
                  max={todayYmd}
                  onChange={(e) => setEntryDate(ymdToDDMMYYYY(e.target.value))}
                  className={inputClass}
                  disabled={loading || pageLoading}
                />
              </div>
            </div>
            <p className={`text-xs mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Effect date must be today or earlier. Percentage must be greater than 0 and not more than 100.
            </p>
          </div>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() =>
                isEdit
                  ? loadEditData()
                  : (setSubtypeId("Select"), setPercentage(""), setEntryDate(todayToDDMMYYYY()))
              }
              disabled={loading || pageLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaUndo />
              {isEdit ? "Reload" : "Clear"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || pageLoading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
            >
              <FaSave />
              {isEdit ? "Update" : "Save"}
            </button>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSpareDeductionEntry;